import React, { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, LogOut, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AdminSidebar from './AdminSidebar';
import { useAuth } from '../../contexts/AuthContext';
import FeedbackWidget from '../Feedback/FeedbackWidget';

const AdminLayout = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const closeSidebar = () => {
        setSidebarOpen(false);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-red-900/20 text-white">
            {/* Header Mobile */}
            <div className="lg:hidden bg-gray-950/90 backdrop-blur-xl border-b border-gray-800/50 p-4 sticky top-0 z-[60]">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={toggleSidebar}
                            className="text-white hover:bg-white/10"
                        >
                            <Menu className="h-5 w-5" />
                        </Button>
                        <div className="flex items-center gap-2">
                            <h1 className="text-lg font-semibold bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400 bg-clip-text text-transparent">
                                Admin Panel
                            </h1>
                            <Shield className="h-4 w-4 text-red-500" />
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-300 hidden sm:inline">{user?.name}</span>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleLogout}
                            className="text-white hover:bg-white/10"
                        >
                            <LogOut className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>

            <div className="flex">
                {/* Sidebar Desktop */}
                <div className="hidden lg:block w-64 h-screen fixed left-0 top-0 z-[100]">
                    <AdminSidebar />
                </div>

                {/* Sidebar Mobile */}
                <AnimatePresence>
                    {sidebarOpen && (
                        <>
                            {/* Overlay */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="lg:hidden fixed inset-0 bg-black/80 z-[110] backdrop-blur-sm"
                                onClick={closeSidebar}
                            />

                            {/* Sidebar Drawer */}
                            <motion.div
                                initial={{ x: -280 }}
                                animate={{ x: 0 }}
                                exit={{ x: -280 }}
                                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                                className="lg:hidden fixed left-0 top-0 w-64 h-full z-[120] shadow-2xl"
                            >
                                <div className="h-full relative">
                                    <AdminSidebar onClose={closeSidebar} />
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={closeSidebar}
                                        className="absolute top-4 right-4 text-white hover:bg-white/10 lg:hidden"
                                    >
                                        <X className="h-5 w-5" />
                                    </Button>
                                </div>
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>

                {/* Conteúdo Principal */}
                <main className="flex-1 lg:ml-64 p-4 lg:p-8 overflow-y-auto min-h-screen w-full">
                    <div className="max-w-7xl mx-auto">
                        <Outlet />
                    </div>
                </main>
            </div>
            <FeedbackWidget />
        </div >
    );
};

export default AdminLayout;
